import { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { EXTERNAL_ROUTES } from "@/constants/external-routes";

export type TFooterLink = {
  title: string;
  href: string;
  analyticsId: string;
  analyticsText?: string;
};
type TFooterNavLink = TFooterLink & {
  mobileMetadata: { analyticsId: string };
};

type TFooterCategory = {
  title: string;
  links: TFooterNavLink[];
};

type TFooterColumn = {
  categories: TFooterCategory[];
};

export function useFooterData() {
  const { t } = useLingui();

  return useMemo(() => {
    const footerData: TFooterColumn[] = [
      {
        categories: [
          {
            title: t`Helpdesk features`,
            links: [
              {
                title: t`Inbox`,
                href: "https://www.intercom.com/helpdesk/inbox",
                analyticsText: "Inbox",
                analyticsId: "link_footer_helpdesk_features_inbox",
                mobileMetadata: {
                  analyticsId: "link_footer_helpdesk_features_inbox_mobile",
                },
              },
              {
                title: t`Live Chat`,
                href: "https://www.intercom.com/live-chat",
                analyticsText: "Live Chat",
                analyticsId: "link_footer_helpdesk_features_live_chat",
                mobileMetadata: {
                  analyticsId: "link_footer_helpdesk_features_live_chat_mobile",
                },
              },
              {
                title: t`Copilot`,
                href: "https://www.intercom.com/helpdesk/copilot",
                analyticsText: "Copilot",
                analyticsId: "link_footer_helpdesk_features_copilot",
                mobileMetadata: {
                  analyticsId: "link_footer_helpdesk_features_copilot_mobile",
                },
              },
              {
                title: t`Tickets`,
                href: "https://www.intercom.com/helpdesk/tickets",
                analyticsText: "Tickets",
                analyticsId: "link_footer_helpdesk_features_tickets",
                mobileMetadata: {
                  analyticsId: "link_footer_helpdesk_features_tickets_mobile",
                },
              },
              {
                title: t`Omnichannel`,
                href: "https://www.intercom.com/helpdesk/omnichannel",
                analyticsText: "Omnichannel",
                analyticsId: "link_footer_helpdesk_features_omnichannel",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_helpdesk_features_omnichannel_mobile",
                },
              },
              {
                title: t`Help Center`,
                href: "https://www.intercom.com/helpdesk/help-center",
                analyticsText: "Help Center",
                analyticsId: "link_footer_helpdesk_features_help_center",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_helpdesk_features_help_center_mobile",
                },
              },
              {
                title: t`Apps & Integrations`,
                href: "https://www.intercom.com/app-store",
                analyticsText: "Apps & Integrations",
                analyticsId: "link_footer_helpdesk_features_apps_integrations",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_helpdesk_features_apps_integrations_mobile",
                },
              },
              {
                title: t`Reporting`,
                href: "https://www.intercom.com/helpdesk/reporting",
                analyticsText: "Reporting",
                analyticsId: "link_footer_helpdesk_features_reporting",
                mobileMetadata: {
                  analyticsId: "link_footer_helpdesk_features_reporting_mobile",
                },
              },
              {
                title: t`Knowledge Hub`,
                href: "https://www.intercom.com/helpdesk/knowledge-hub",
                analyticsText: "Knowledge Hub",
                analyticsId: "link_footer_helpdesk_features_knowledge_hub",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_helpdesk_features_knowledge_hub_mobile",
                },
              },
              {
                title: t`Outbound`,
                href: "https://www.intercom.com/helpdesk/outbound",
                analyticsText: "Outbound",
                analyticsId: "link_footer_helpdesk_features_outbound",
                mobileMetadata: {
                  analyticsId: "link_footer_helpdesk_features_outbound_mobile",
                },
              },
            ],
          },
        ],
      },
      {
        categories: [
          {
            title: t`Fin features`,
            links: [
              {
                title: t`Fin overview`,
                href: "https://fin.ai",
                analyticsText: "Fin overview",
                analyticsId: "link_footer_fin_features_fin_overview",
                mobileMetadata: {
                  analyticsId: "link_footer_fin_features_fin_overview_mobile",
                },
              },
              {
                title: t`Capabilities`,
                href: "https://fin.ai/capabilities",
                analyticsText: "Capabilities",
                analyticsId: "link_footer_fin_features_capabilities",
                mobileMetadata: {
                  analyticsId: "link_footer_fin_features_capabilities_mobile",
                },
              },
              {
                title: t`AI Engine`,
                href: "https://fin.ai/ai-engine",
                analyticsText: "AI Engine",
                analyticsId: "link_footer_fin_features_ai_engine",
                mobileMetadata: {
                  analyticsId: "link_footer_fin_features_ai_engine_mobile",
                },
              },
            ],
          },
          {
            title: t`Pricing`,
            links: [
              {
                title: t`Intercom pricing`,
                href: "https://www.intercom.com/pricing",
                analyticsText: "Intercom pricing",
                analyticsId: "link_footer_pricing_intercom_pricing",
                mobileMetadata: {
                  analyticsId: "link_footer_pricing_intercom_pricing_mobile",
                },
              },
              {
                title: t`Fin pricing`,
                href: "https://fin.ai/pricing",
                analyticsText: "Fin pricing",
                analyticsId: "link_footer_pricing_fin_pricing",
                mobileMetadata: {
                  analyticsId: "link_footer_pricing_fin_pricing_mobile",
                },
              },
            ],
          },
          {
            title: t`Solutions`,
            links: [
              {
                title: t`Technology`,
                href: "https://www.intercom.com/solutions/technology",
                analyticsText: "Technology",
                analyticsId: "link_footer_solutions_technology",
                mobileMetadata: {
                  analyticsId: "link_footer_solutions_technology_mobile",
                },
              },
              {
                title: t`Ecommerce`,
                href: "https://www.intercom.com/solutions/ecommerce",
                analyticsText: "Ecommerce",
                analyticsId: "link_footer_solutions_ecommerce",
                mobileMetadata: {
                  analyticsId: "link_footer_solutions_ecommerce_mobile",
                },
              },
              {
                title: t`Fintech`,
                href: "https://www.intercom.com/solutions/fintech",
                analyticsText: "Fintech",
                analyticsId: "link_footer_solutions_fintech",
                mobileMetadata: {
                  analyticsId: "link_footer_solutions_fintech_mobile",
                },
              },
              {
                title: t`Gaming`,
                href: "https://www.intercom.com/solutions/gaming",
                analyticsText: "Gaming",
                analyticsId: "link_footer_solutions_gaming",
                mobileMetadata: {
                  analyticsId: "link_footer_solutions_gaming_mobile",
                },
              },
            ],
          },
        ],
      },
      {
        categories: [
          {
            title: t`Learn`,
            links: [
              {
                title: t`Events`,
                href: "https://events.intercom.com/",
                analyticsText: "Events",
                analyticsId: "link_footer_learn_events",
                mobileMetadata: {
                  analyticsId: "link_footer_learn_events_mobile",
                },
              },
              {
                title: t`Intercom blog`,
                href: "https://www.intercom.com/blog",
                analyticsText: "Intercom blog",
                analyticsId: "link_footer_learn_intercom_blog",
                mobileMetadata: {
                  analyticsId: "link_footer_learn_intercom_blog_mobile",
                },
              },
              {
                title: t`Academy`,
                href: "https://academy.fin.ai",
                analyticsText: "Academy",
                analyticsId: "link_footer_learn_academy",
                mobileMetadata: {
                  analyticsId: "link_footer_learn_academy_mobile",
                },
              },
              {
                title: t`YouTube`,
                href: "https://www.youtube.com/channel/UCJG0MvLP03kyzzAkD-w98aQ",
                analyticsText: "YouTube",
                analyticsId: "link_footer_learn_youtube",
                mobileMetadata: {
                  analyticsId: "link_footer_learn_youtube_mobile",
                },
              },
              {
                title: t`AI Agent Blueprint`,
                href: "https://fin.ai/blueprint",
                analyticsText: "AI Agent Blueprint",
                analyticsId: "link_footer_learn_ai_agent_blueprint",
                mobileMetadata: {
                  analyticsId: "link_footer_learn_ai_agent_blueprint_mobile",
                },
              },
              {
                title: t`Learning Center`,
                href: "https://www.intercom.com/learning-center",
                analyticsText: "Learning Center",
                analyticsId: "link_footer_learn_learning_center",
                mobileMetadata: {
                  analyticsId: "link_footer_learn_learning_center_mobile",
                },
              },
              {
                title: t`2026 Transformation Report`,
                href: "/customer-transformation-report",
                analyticsText: "2026 Transformation Report",
                analyticsId: "link_footer_learn_2026_transformation_report",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_learn_2026_transformation_report_mobile",
                },
              },
            ],
          },
          {
            title: t`Evaluate`,
            links: [
              {
                title: t`Why choose Intercom`,
                href: "https://www.intercom.com/why-choose-intercom",
                analyticsText: "Why choose Intercom",
                analyticsId: "link_footer_evaluate_why_choose_intercom",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_evaluate_why_choose_intercom_mobile",
                },
              },
              {
                title: t`Safety & security`,
                href: "https://trust.intercom.com/",
                analyticsText: "Safety & security",
                analyticsId: "link_footer_evaluate_safety_security",
                mobileMetadata: {
                  analyticsId: "link_footer_evaluate_safety_security_mobile",
                },
              },
              {
                title: t`ROI calculator`,
                href: "https://fin.ai/roi-calculator",
                analyticsText: "ROI calculator",
                analyticsId: "link_footer_evaluate_roi_calculator",
                mobileMetadata: {
                  analyticsId: "link_footer_evaluate_roi_calculator_mobile",
                },
              },
              {
                title: t`Customer case studies`,
                href: "https://www.intercom.com/customers",
                analyticsText: "Customer case studies",
                analyticsId: "link_footer_evaluate_customer_case_studies",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_evaluate_customer_case_studies_mobile",
                },
              },
            ],
          },
        ],
      },
      {
        categories: [
          {
            title: t`Support`,
            links: [
              {
                title: t`Changes`,
                href: "https://www.intercom.com/changes/en",
                analyticsText: "Changes",
                analyticsId: "link_footer_support_changes",
                mobileMetadata: {
                  analyticsId: "link_footer_support_changes_mobile",
                },
              },
              {
                title: t`Help Center`,
                href: "https://www.intercom.com/help/en/",
                analyticsText: "Help Center",
                analyticsId: "link_footer_support_help_center",
                mobileMetadata: {
                  analyticsId: "link_footer_support_help_center_mobile",
                },
              },
              {
                title: t`Developer hub`,
                href: "https://developers.intercom.com/",
                analyticsText: "Developer hub",
                analyticsId: "link_footer_support_developer_hub",
                mobileMetadata: {
                  analyticsId: "link_footer_support_developer_hub_mobile",
                },
              },
              {
                title: t`Intercom community`,
                href: "https://community.intercom.com/",
                analyticsText: "Intercom community",
                analyticsId: "link_footer_support_intercom_community",
                mobileMetadata: {
                  analyticsId: "link_footer_support_intercom_community_mobile",
                },
              },
              {
                title: t`Status`,
                href: "https://www.finstatus.com/",
                analyticsText: "Status",
                analyticsId: "link_footer_support_status",
                mobileMetadata: {
                  analyticsId: "link_footer_support_status_mobile",
                },
              },
            ],
          },
          {
            title: t`Company`,
            links: [
              {
                title: t`About`,
                href: "https://www.intercom.com/about",
                analyticsText: "About",
                analyticsId: "link_footer_company_about",
                mobileMetadata: {
                  analyticsId: "link_footer_company_about_mobile",
                },
              },
              {
                title: t`Careers`,
                href: "https://fin.ai/careers",
                analyticsText: "Careers",
                analyticsId: "link_footer_company_careers",
                mobileMetadata: {
                  analyticsId: "link_footer_company_careers_mobile",
                },
              },
              {
                title: t`Contact Press`,
                href: "mailto:press@intercom.com",
                analyticsText: "Contact Press",
                analyticsId: "link_footer_company_contact_press",
                mobileMetadata: {
                  analyticsId: "link_footer_company_contact_press_mobile",
                },
              },
            ],
          },
        ],
      },
      {
        categories: [
          {
            title: t`Programs`,
            links: [
              {
                title: t`Solution partner`,
                href: "https://www.intercom.com/solution-partner-program",
                analyticsText: "Solution partner",
                analyticsId: "link_footer_programs_solution_partner",
                mobileMetadata: {
                  analyticsId: "link_footer_programs_solution_partner_mobile",
                },
              },
              {
                title: t`Technology partner`,
                href: "https://www.intercom.com/technology-partner-program",
                analyticsText: "Technology partner",
                analyticsId: "link_footer_programs_technology_partner",
                mobileMetadata: {
                  analyticsId: "link_footer_programs_technology_partner_mobile",
                },
              },
              {
                title: t`Early stage`,
                href: "https://www.intercom.com/early-stage",
                analyticsText: "Early stage",
                analyticsId: "link_footer_programs_early_stage",
                mobileMetadata: {
                  analyticsId: "link_footer_programs_early_stage_mobile",
                },
              },
            ],
          },
          {
            title: t`Intercom in action`,
            links: [
              {
                title: t`View demo`,
                href: "https://www.intercom.com/view-demos",
                analyticsText: "View demo",
                analyticsId: "link_footer_intercom_in_action_view_demo",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_intercom_in_action_view_demo_mobile",
                },
              },
              {
                title: t({ message: "Free trial", context: "navigation" }),
                href: EXTERNAL_ROUTES.REACT_GENERIC_START_FREE_TRIAL,
                analyticsText: "Free trial",
                analyticsId: "link_footer_intercom_in_action_free_trial",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_intercom_in_action_free_trial_mobile",
                },
              },
              {
                title: t`Contact sales`,
                href: "https://www.intercom.com/contact-sales",
                analyticsText: "Contact sales",
                analyticsId: "link_footer_intercom_in_action_contact_sales",
                mobileMetadata: {
                  analyticsId:
                    "link_footer_intercom_in_action_contact_sales_mobile",
                },
              },
              {
                title: t`Sign in`,
                href: "https://app.intercom.com/admins/sign_in",
                analyticsText: "Sign in",
                analyticsId: "link_footer_intercom_in_action_sign_in",
                mobileMetadata: {
                  analyticsId: "link_footer_intercom_in_action_sign_in_mobile",
                },
              },
            ],
          },
        ],
      },
    ];

    const footerInfoLinks: TFooterLink[] = [
      {
        title: t`Terms`,
        href: "https://www.intercom.com/legal/terms-and-policies",
        analyticsText: "Terms",
        analyticsId: "link_footer_info_terms",
      },
      {
        title: t`Privacy`,
        href: "https://www.intercom.com/legal/privacy",
        analyticsText: "Privacy",
        analyticsId: "link_footer_info_privacy",
      },
      {
        title: t`Security`,
        href: "https://trust.intercom.com/",
        analyticsText: "Security",
        analyticsId: "link_footer_info_security",
      },
    ];

    return { footerData, footerInfoLinks };
  }, [t]);
}
